import type { ResponseSuccess } from '../../../shared/types';
import type {
  DwdConnectedEmail,
  DwdConnectedEmailsResponse,
  DwdSyncPreviewResponse,
  DwdWorkspaceListItem,
} from '../types';

export function getWorkspacesFromResponse(
  response: ResponseSuccess | DwdWorkspaceListItem[] | null | undefined,
): DwdWorkspaceListItem[] {
  if (Array.isArray(response)) {
    return response;
  }

  const payload = response?.payload;
  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

export function getGoogleWorkspaces(
  workspaces: DwdWorkspaceListItem[],
): DwdWorkspaceListItem[] {
  return workspaces.filter((workspace) => workspace.provider === 'google');
}

export function getMicrosoftWorkspaces(
  workspaces: DwdWorkspaceListItem[],
): DwdWorkspaceListItem[] {
  return workspaces.filter((workspace) => workspace.provider === 'microsoft');
}

export function getConsentUrlFromResponse(
  response: ResponseSuccess | null | undefined,
): string | null {
  const data = response?.payload ?? response;
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { url } = data as { url?: string };
  if (typeof url === 'string' && url.trim()) {
    return url.trim();
  }

  return null;
}

export function getWorkspaceSummary(
  workspace: DwdWorkspaceListItem,
): {
  domainCount: number;
  mailboxCount: number;
} {
  const domainCount = workspace.domains.length;
  const mailboxCount = workspace.domains.reduce(
    (sum, domain) => sum + domain.mailboxCount,
    0,
  );

  return { domainCount, mailboxCount };
}

export function getWorkspaceTagLabel(workspace: DwdWorkspaceListItem): string {
  if (workspace.tagName?.trim()) {
    return workspace.tagName.trim();
  }
  return null;
}

export function getWorkspaceIdFromResponse(
  response: ResponseSuccess | null | undefined,
): string | null {
  const data = response?.payload ?? response;
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { workspaceId } = data as { workspaceId?: string; id?: string };
  if (typeof workspaceId === 'string' && workspaceId.trim()) {
    return workspaceId;
  }

  const { id } = data as { id?: string };
  if (typeof id === 'string' && id.trim()) {
    return id;
  }

  return null;
}

export function getSyncPreviewFromResponse(
  response: ResponseSuccess | null | undefined,
): DwdSyncPreviewResponse | null {
  const data = response?.payload ?? response;
  if (!data || typeof data !== 'object') {
    return null;
  }

  const preview = data as DwdSyncPreviewResponse;
  if (!Array.isArray(preview.newEmails)) {
    return null;
  }

  const disabledInPage = preview.newEmails.filter((e) => e.disabled).length;
  const connectedCount = Math.max(preview.connectedCount ?? 0, disabledInPage);

  return { ...preview, connectedCount };
}

export function getDomainEmailsKey(
  workspaceId: string,
  domain: string,
): string {
  return `${workspaceId}:${domain}`;
}

export function getConnectedEmailsFromResponse(
  response: ResponseSuccess | DwdConnectedEmailsResponse | null | undefined,
): DwdConnectedEmailsResponse | null {
  if (response && typeof response === 'object' && 'data' in response) {
    const direct = response as DwdConnectedEmailsResponse;
    if (Array.isArray(direct.data)) {
      return direct;
    }
  }

  const payload = (response as ResponseSuccess | null | undefined)?.payload;
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const emails = payload as DwdConnectedEmailsResponse;
  if (!Array.isArray(emails.data)) {
    return null;
  }

  return emails;
}

export function isDwdEmailConnected(email: DwdConnectedEmail): boolean {
  return String(email.status) === '1';
}

export function getDwdWorkspaceProviderIconId(
  provider?: string | null,
): 'google-workspace-bulk' | 'microsoft-bulk' {
  if (provider === 'microsoft') {
    return 'microsoft-bulk';
  }

  return 'google-workspace-bulk';
}
